<script lang="ts" setup>
import { PropType, ref, computed } from "vue";
import { Dropdown, Breadcrumbs } from "@/components";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import AddContent from "./AddContent.vue";
import { type Config } from "./Config";

const { t } = useI18n();
const router = useRouter();

const props = defineProps({
  currentConfig: {
    type: Object as PropType<Config>,
    required: true,
  },
  currentLanguage: {
    type: String,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
});

const emits = defineEmits([
  "newContent",
  "switchLanguage",
  "reloadData",
  "displaySettings",
]);

const navItems = computed(() => [
  {
    text: "Shared Space Templates",
    href: router.resolve({ name: "SettingsOverview" }).fullPath,
  },
  {
    text: props.name,
  },
]);

const emitNewContent = (contentType: string) => {
  emits("newContent", contentType);
};

const languages = computed(() => {
  return props.currentConfig.languages.map((l) => ({
    title:
      props.currentConfig.defaultLanguage === l
        ? t(`templateEditor.languages.${l}.defaultLabel`)
        : t(`templateEditor.languages.${l}.label`),
    value: props.currentConfig.defaultLanguage === l ? "default" : l,
  }));
});

const switchLanguage = (value: string) => {
  emits("switchLanguage", value);
};
</script>

<template>
  <div class="editor_navbar">
    <breadcrumbs :items="navItems" :title="name" />

    <div class="right controls">
      <Dropdown
        v-if="languages.length > 1"
        :items="languages"
        @change="switchLanguage"
        :value="currentLanguage"
        :displayTitle="true"
      />

      <add-content
        :config="currentConfig"
        :currentLanguage="currentLanguage"
        @submit="emitNewContent"
      />

      <div
        class="control"
        role="button"
        tabindex="0"
        :aria-label="$t('templateEditor.toolbar.settings.title')"
        @keydown.enter.prevent="emits('displaySettings')"
        @keydown.space.prevent="emits('displaySettings')"
        @click="emits('displaySettings')"
      >
        <span class="material-symbols-outlined">settings</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.editor_navbar {
  height: 55px;
  border-bottom: 1px solid var(--classic-border-subtle, #e0e0e0);
  padding: 0 1.5em;
  display: flex;
  align-items: center;
  background: var(--classic-surface, #fff);
}

.editor_navbar .breadcrumbs {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
}

.editor_navbar .breadcrumbs :deep(.item:first-child) {
  flex-shrink: 0;
}

// A long template name must not wrap above the header or push out its controls.
.editor_navbar .breadcrumbs :deep(.item:last-child) {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.controls {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  font-size: 0.9rem;
}

.control .material-symbols-outlined {
  font-size: 1.3em;
  cursor: pointer;
  border-radius: 50%;
  padding: 0.5em;
  line-height: 1;
  transition: background 0.2s ease;
  margin-left: 0.2em;
}

.control .material-symbols-outlined:hover {
  background: var(--classic-surface-hover, #f4f4f4);
}

.controls .dropdown {
  z-index: 100;
  margin-left: 0.8em;
  margin-right: 0.5em;
}

.controls .dropdown :deep(.dropdown_title) {
  border-radius: 8px;
}
</style>
